/**
 * Name Step Modal
 *
 * Second step of onboarding - collects user's name
 * Figma: Property 1=Sign Up 2
 * Step 2 of 4 (removed sizing step)
 */
"use client";

import { useEffect, useState } from "react";

import { colors, modalSpacing } from "@/styles/design-tokens";

import { BasicModalShell } from "./BasicModalShell";
import { ModalPrimaryButton } from "./ModalPrimaryButton";
import { ModalProgressBar } from "./ModalProgressBar";
import { PreviousSection } from "./PreviousSection";

const SPACING = {
  // Use a clamp so small screens don't waste vertical space
  topPadding: "clamp(56px, 12vh, 119px)", // From Figma: top position of content
  labelInputGap: "8px", // Gap between label and input
  inputButtonGap: "28px", // Gap between input and Next button
  previousPaddingTop: "50px", // Space between divider and PREVIOUS button
  previousPaddingBottom: "16px", // Padding below PREVIOUS button (reduced)
} as const;

interface NameStepModalProps {
  isOpen: boolean;
  onClose: () => void;
  onNext: (firstName: string) => void;
  onBack: () => void;
  initialFirstName?: string;
  currentStep?: number;
  totalSteps?: number;
}

export function NameStepModal({
  isOpen,
  onClose,
  onNext,
  onBack,
  initialFirstName = "",
  currentStep = 2,
  totalSteps = 4,
}: NameStepModalProps) {
  const [firstName, setFirstName] = useState(initialFirstName);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (isOpen && initialFirstName) {
      setFirstName(initialFirstName);
    }
  }, [isOpen, initialFirstName]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (firstName.trim()) {
      onNext(firstName.trim());
    }
  };

  return (
    <BasicModalShell
      isOpen={isOpen}
      onClose={onClose}
      closeOnBackdropClick={false}
      panelClassName="sm:min-h-[520px]"
      topSlot={
        <ModalProgressBar
          currentStep={currentStep}
          totalSteps={totalSteps}
          animateFromPrevious={true}
        />
      }
    >
      <div
        className="mx-auto flex w-full flex-col px-4 sm:px-0"
        style={{
          maxWidth: modalSpacing.signInContentWidth,
          gap: modalSpacing.sectionGap,
          paddingTop: SPACING.topPadding,
          paddingBottom: modalSpacing.contentBottomPadding,
        }}
      >
        {/* Header with Title and Subtitle */}
        <div
          className="flex flex-col items-center text-center"
          style={{ gap: modalSpacing.headerGap }}
        >
          <h2 className="text-title text-ink">What should we call you?</h2>
          <p className="text-caption text-ink-secondary">
            This helps us personalize your shopping experience.
          </p>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="flex flex-col"
          style={{ gap: SPACING.inputButtonGap }}
        >
          {/* Preferred Name Input with Label Above */}
          <div className="flex flex-col" style={{ gap: SPACING.labelInputGap }}>
            {/* Label Above Input */}
            <label className="text-caption text-ink-secondary">Preferred Name</label>

            {/* Input Field */}
            <div className="relative w-full" style={{ height: "44.5px" }}>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                placeholder="Preferred Name"
                className="text-input-modal text-ink w-full bg-transparent outline-none"
                style={{
                  border: "none",
                  borderBottom: `1px solid ${colors.divider.divider1}`,
                  borderRadius: "0",
                  paddingTop: "12px",
                  paddingBottom: "8px",
                  paddingLeft: "0",
                  paddingRight: "0",
                  height: "44.5px",
                }}
                aria-label="Preferred name"
                autoFocus
              />
              {/* Active underline */}
              <div
                className="absolute bottom-0 left-0 h-[0.5px] bg-black transition-all duration-200"
                style={{ width: focused ? "100%" : "0" }}
              />
            </div>
          </div>

          {/* Next Button */}
          <ModalPrimaryButton type="submit" disabled={!firstName.trim()}>
            Next
          </ModalPrimaryButton>
        </form>

        {/* Previous Section */}
        <PreviousSection
          onBack={onBack}
          paddingTop={SPACING.previousPaddingTop}
          paddingBottom={SPACING.previousPaddingBottom}
        />
      </div>
    </BasicModalShell>
  );
}
